import Title from "../components/Title";
import LevelCycleButton from "../components/Course/LevelCycleButton";
import { Filters, callWithAuth, getAllChapters } from "@/api/Api";
import { mapCoursesToDisplayCourses } from "../lib/mapCoursesToDisplayCourse";
import CoursesCardRectangle from "../components/Cards/CoursesCardRectangle";
import CourseHighlight from "../components/CourseHighlight";
import HorizontalScroll from "../components/HorizontalScroll";
import PillButton from "../components/Buttons/PillButton";
import { useCourseFilters } from "@/hooks/useCourseFilters";
import { useCourses, DisplayCourse } from "@/hooks/useCourses";
import { useCurrentCourse } from "@/hooks/useSelectedCourse";
import { useUser } from "@/hooks/UseAuthProviders";
import {
  CEFR_TO_DIFFICULTY_MAP,
  CEFRLevel,
} from "@/components/PlacementTest/levelMapping";
import { useActiveCourseFilters } from "@/hooks/useActiveCourseFilters";
import { useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTrackCourseSelected } from "../hooks/posthog/useTrackCourseSelected";
import { useTrackLevelChange } from "../hooks/posthog/useTrackLevelChange";
import { useTrackTopicsChange } from "../hooks/posthog/useTrackTopicsChange";
import { useTrackCoursePageViewed } from "@/hooks/posthog/useTrackCoursePageViewed";
import { useNavigate } from "react-router-dom";
import { LoadingPage } from "./LoadingPage";
import {
  FLIGHT_SCHOOL_COURSE_ID,
  createFlightSchoolDisplayCourse,
  useFlightSchoolCourse,
} from "@/features/flight-school";

const CoursesPage = () => {
  const { selectedLevel, selectedTopics, setFilters, isLoading: isFiltersLoading } =
    useActiveCourseFilters();
  // Take the loading flag from useCurrentCourse (NOT useCourses): only the former
  // accounts for the most-recent-course DERIVATION. useCourses().isCurrentCourseLoading
  // reflects just the localStorage read, which resolves before the derived course
  // id is known — letting the page (and the level auto-init below) run early.
  const { currentCourseID, setCurrentCourseID, isCurrentCourseLoading } =
    useCurrentCourse();
  const { user } = useUser();
  const {
    coursesProgressData,
    currentCourseData,
    isCoursesLoading,
    isCoursesProgressLoading,
  } = useCourses();
  const { topicsData, levelsData, levelsLoading } = useCourseFilters();
  const topics = topicsData?.topics;
  const rectableCourseContainerRef = useRef<HTMLDivElement>(null);
  const trackCourseSelected = useTrackCourseSelected();
  const trackLevelChange = useTrackLevelChange();
  const trackTopicsChange = useTrackTopicsChange();
  const nav = useNavigate();

  const { completedModules: fsCompletedModules, modules: fsModules } =
    useFlightSchoolCourse();
  const isInFlightSchool = currentCourseID === FLIGHT_SCHOOL_COURSE_ID;

  // tracks the course page viewed event via posthog
  useTrackCoursePageViewed();

  // Fetch the current course's level so we can auto-init the level filter
  // (Flight School is virtual — difficulty 1 — so skip the query for it).
  const currentCourseInfo = useQuery({
    queryKey: ["courseInfo", currentCourseID],
    // `enabled` guards that currentCourseID is defined before this runs; the
    // `|| 1` fallback (matching LearnPage) just keeps the type a concrete
    // number without a cast — it's never actually sent.
    queryFn: () =>
      callWithAuth(getAllChapters, {
        courseId: currentCourseID || 1,
      }),
    enabled:
      currentCourseID !== undefined &&
      currentCourseID !== FLIGHT_SCHOOL_COURSE_ID,
  });

  // Auto-init the level filter to the current course's level (difficulty),
  // one time only. The ref keeps manual cycling from being clobbered.
  // Mirrors client-native CoursesScreen auto-init effect.
  const hasAutoInitializedLevel = useRef(false);
  useEffect(() => {
    if (hasAutoInitializedLevel.current) return;
    if (!user || isFiltersLoading) return;
    // Block until the current course id is actually known. While the most-recent
    // derivation loads, currentCourseID is undefined and currentCourseInfo is
    // disabled — so without this we'd auto-init off the placement fallback and
    // lock in the wrong level before the derived course's real level arrives.
    if (currentCourseID === undefined) return;
    // …and for a real course, also wait for its level query to finish loading.
    // Otherwise the id resolves, currentCourseInfo is still loading (data
    // undefined), and we'd fall back to the placement CEFR level + set the
    // one-shot flag before the course's actual difficulty arrives. Once it
    // settles (success OR error) we proceed, using placement only as a genuine
    // fallback. (FS has no such query.)
    if (!isInFlightSchool && currentCourseInfo.isLoading) return;

    // Flight School is a virtual course (difficulty 1) with no backend record,
    // so currentCourseInfo is disabled for it — handle it explicitly. Falls
    // back to the user's placement result when no current course level exists.
    const difficultyLevel = isInFlightSchool
      ? 1
      : currentCourseInfo.data?.course?.level?.difficulty ??
        (user.placementResult
          ? CEFR_TO_DIFFICULTY_MAP[user.placementResult as CEFRLevel]
          : undefined);

    if (difficultyLevel === undefined) return;

    hasAutoInitializedLevel.current = true;

    if (selectedLevel === 0 && difficultyLevel > 0) {
      setFilters({
        selectedLevel: difficultyLevel,
        selectedTopics: selectedTopics,
      });
    }
  }, [
    user,
    isFiltersLoading,
    isInFlightSchool,
    currentCourseID,
    currentCourseInfo.data,
    currentCourseInfo.isLoading,
    selectedLevel,
    selectedTopics,
    setFilters,
  ]);

  // Wait for auto-init to complete before showing the course list so the
  // filter button label and the filtered results are always in sync
  // (mirrors client-native CoursesScreen isAutoInitPending).
  const isAutoInitPending =
    !hasAutoInitializedLevel.current &&
    selectedLevel === 0 &&
    !isFiltersLoading &&
    (isInFlightSchool || currentCourseInfo.isLoading);

  const courses: DisplayCourse[] = useMemo(() => {
    if (coursesProgressData?.courses && currentCourseData) {
      return mapCoursesToDisplayCourses(coursesProgressData, currentCourseData);
    }
    return [];
  }, [coursesProgressData, currentCourseData]);

  // Flight School virtual course (difficulty 1).
  const flightSchoolCourse = useMemo(
    () =>
      createFlightSchoolDisplayCourse(
        fsCompletedModules,
        isInFlightSchool,
        fsModules
      ),
    [fsCompletedModules, isInFlightSchool, fsModules]
  );

  // Shown for "All" (0/null) and "Super Beginner" (difficulty 1) — iOS rule.
  const shouldShowFlightSchool =
    selectedLevel === null || selectedLevel === 0 || selectedLevel === 1;

  // Filter by difficulty (matching iOS) — NOT level.id.
  const filteredCourses = useMemo(() => {
    const filtered = courses
      .filter((course: DisplayCourse) => {
        const levelMatch =
          selectedLevel === null ||
          selectedLevel === 0 ||
          course.level.difficulty === selectedLevel;
        const topicMatch =
          selectedTopics.length === 0 ||
          selectedTopics.includes(Number(course.topic));
        return levelMatch && topicMatch;
      })
      .sort((a, b) => a.name.localeCompare(b.name));

    return shouldShowFlightSchool
      ? [flightSchoolCourse, ...filtered]
      : filtered;
  }, [
    courses,
    selectedLevel,
    selectedTopics,
    shouldShowFlightSchool,
    flightSchoolCourse,
  ]);

  // Add loading state
  if (
    isCoursesLoading ||
    isCurrentCourseLoading ||
    isCoursesProgressLoading ||
    isAutoInitPending
  ) {
    return (
      <div className="fixed inset-2 flex items-center justify-center">
        <LoadingPage showText={false} />
      </div>
    );
  }

  // Mirrors iOS CoursesScreen.handleSelectCourse: switching a course (real or
  // Flight School) sets it current, invalidates the course queries
  // (setCurrentCourseID handles both, like switchToCourse), and lands on the
  // Home tab's chapter path — never the /course/:id detail screen, which on
  // iOS is only reachable via the no-chapters fallback.
  const handleSelectCourse = async (courseId: number) => {
    try {
      trackCourseSelected();
      await setCurrentCourseID(courseId);
      nav("/home/learn");
    } catch (error) {
      console.error("Failed to select course:", error);
    }
  };

  const handleFilterChange = (filters: Filters) => {
    if (filters.selectedLevel !== selectedLevel) {
      trackLevelChange(filters.selectedLevel);
    }
    if (filters.selectedTopics !== selectedTopics) {
      trackTopicsChange(filters.selectedTopics);
    }
    setFilters(filters);
  };

  return (
    <div className="flex flex-col justify-start w-full min-w-0 max-w-[600px] mx-auto h-full gap-4 pt-14">
      <div className="flex flex-col relative">
        <Title text="Courses" />
        <div className="text-[var(--color-grey)] text-left">
          Pick up where you left off
        </div>
        <div className="absolute right-[1px] top-2">
          <LevelCycleButton
            selectedLevel={selectedLevel}
            levelsData={levelsData}
            levelsLoading={levelsLoading}
            onLevelChange={(level) =>
              handleFilterChange({
                selectedLevel: level,
                selectedTopics: selectedTopics,
              })
            }
          />
        </div>
      </div>
      {filteredCourses?.length > 0 ? (
        <CourseHighlight courses={filteredCourses} onClick={handleSelectCourse} />
      ) : (
        <div className="flex flex-col items-center justify-center w-full h-[--large-card-width] p-8 bg-white rounded-xl border border-[var(--color-grey-light)]">
          <div className="text-7xl mb-2">📚</div>
          <div className="text-xl font-semibold text-[var(--color-grey)]">
            No courses found
          </div>
          <div className="text-base font-normal text-[var(--color-grey)]">
            Try adjusting your filters to see more courses
          </div>
        </div>
      )}

      <HorizontalScroll className="w-full min-w-0">
        <div className="flex flex-row gap-2 py-1">
          <PillButton
            label="All Topics"
            isSelected={selectedTopics.length === 0}
            onClick={() =>
              handleFilterChange({
                selectedLevel: selectedLevel,
                selectedTopics: [],
              })
            }
          />
          {topics?.map((topic) => (
            <PillButton
              key={topic.id}
              label={topic.name}
              isSelected={selectedTopics.includes(topic.id)}
              onClick={() => {
                if (selectedTopics.includes(topic.id)) {
                  const newTopics = selectedTopics.filter(
                    (id) => id !== topic.id
                  );
                  handleFilterChange({
                    selectedLevel: selectedLevel,
                    selectedTopics: newTopics,
                  });
                } else {
                  handleFilterChange({
                    selectedLevel: selectedLevel,
                    selectedTopics: [...selectedTopics, topic.id],
                  });
                }
              }}
            />
          ))}
        </div>
      </HorizontalScroll>

      <div
        ref={rectableCourseContainerRef}
        className="flex flex-col gap-2 -mt-1 pb-3"
      >
        {filteredCourses.map((course) => (
          <CoursesCardRectangle
            key={course.courseId}
            emoji={course.icon || "📚"}
            selected={course.id === currentCourseID}
            title={course.name}
            subtitle={course.description}
            level={course.level}
            progress={course.progress}
            onClick={() => handleSelectCourse(course.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default CoursesPage;
